// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Clones the track once for a gapless loop and animates with the Web Animations API.
// Respects prefers-reduced-motion (no animation) and pauses on hover. One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #resumeTimer = null;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal: this.#ac.signal });

    this.addEventListener("mouseenter", () => this.#animation?.pause(), { signal: this.#ac.signal });
    this.addEventListener("mouseleave", () => this.#animation?.play(), { signal: this.#ac.signal });
    this.addEventListener("focusin", () => this.#animation?.pause(), { signal: this.#ac.signal });
    this.addEventListener("focusout", () => this.#animation?.play(), { signal: this.#ac.signal });

    // Touch: pause while the visitor swipes, then resume a few seconds after they stop.
    const pauseForTouch = () => {
      clearTimeout(this.#resumeTimer);
      this.#animation?.pause();
    };
    const resumeLater = () => {
      clearTimeout(this.#resumeTimer);
      this.#resumeTimer = setTimeout(() => {
        if (!this.matches(":focus-within")) this.#animation?.play();
      }, 3000);
    };
    this.addEventListener("touchstart", pauseForTouch, { passive: true, signal: this.#ac.signal });
    this.addEventListener("touchend", resumeLater, { passive: true, signal: this.#ac.signal });
    this.addEventListener("scroll", () => {
      pauseForTouch();
      resumeLater();
    }, { passive: true, signal: this.#ac.signal });

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    clearTimeout(this.#resumeTimer);
    this.#animation?.cancel();
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track || this.#mql.matches) return;

    // Ensure exactly two copies of the content so the loop is seamless.
    if (!track.dataset.cloned) {
      const clones = [...track.children].map((n) => {
        const clone = n.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        clone.querySelectorAll("a, button").forEach((el) => el.setAttribute("tabindex", "-1"));
        if (clone.matches("a, button")) clone.setAttribute("tabindex", "-1");
        return clone;
      });
      track.append(...clones);
      track.dataset.cloned = "true";
    }

    // One loop = one copy of the content plus one gap, so the seam lines up exactly.
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const distance = (track.scrollWidth + gap) / 2;
    if (distance <= 0) return;

    const speed = parseFloat(this.dataset.speed) || 40;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
